import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, shareReplay, take, takeWhile } from 'rxjs/operators';
import { AuthenticationService } from './AuthenticationService';
import { AuctionManagementApiService } from './AuctionManagementApiService';
import { BidPublishingData } from '../models/bidPublishingData';
import { Property } from '../models/property';
import { AuctionBid } from '../models/auctionBid';
import { BidRequest } from '../requests/bidRequest';
import { BidResponse } from '../models/bidResult';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Bid } from '../models/bid';
import { AuthorizationApiService } from './AuthorizationApiService';
import { AuctionAuthorizationRequest } from '../requests/auctionAuthorizationRequest';
import { Result } from '../models/result';
import { AcceptRequest } from '../requests/acceptRequest';
import { AuctionStatusPublishingData } from '../models/auctionStatusPublishingData';
import { CreatePaymentIntentStripeRequest } from '../requests/stripe/createPaymentIntentStripeRequest';
import { CreatePaymentIntentStripeResponse } from '../responses/stripe/createPaymentIntentStripeResponse';
import { CreatePaymentIntentPaypalRequest } from '../requests/paypal/createPaymentIntentPaypalRequest';
import { CreatePaymentIntentPaypalResponse } from '../responses/paypal/createPaymentIntentPaypalResponse';
import { AuctionBidDTO } from '../DTO/auctionBidDTO';
import { ApplicationUser } from '../models/applicationUser';
import { PropertyBidHistoryDTO } from '../DTO/propertyBidHistoryDTO';
import { PropertyBidHistoryAnonymized } from '../models/propertyBidHistoryAnonymized';
import { PropertyBidHistoryAnonymizedDTO } from '../DTO/propertyBidHistoryAnonymizedDTO';
import { PropertyBidHistory } from '../models/propertyBidHistory';
import { AuctionPaymentRequest } from '../requests/auctionPaymentRequest';
import { PrivateAuctionAuthorizationRequest } from '../requests/privateAuctionAuthorizationRequest';
import { PrivateAuctionAuthorizationResponse } from '../responses/privateAuctionauthorizationResponse';
import { LinkTokenResponse } from '../responses/plaid/linkTokenResponse';
import { UpdatePrivateAuctionAuthorizationStatusRequest } from '../requests/updatePrivateAuctionAuthorizationStatusRequest';
import { PrivateAuctionAuthorizationDTO } from '../DTO/privateAuctionAuthorizationDTO';
import { EqualizeUserBidForAuctionRequest } from '../requests/equalizeUserBidForAuctionRequest';
import { BasicResponse } from '../responses/basicResponse';
import { PropertyService } from './PropertyService';
import { UserAuctionAuthorizationStatusRequest } from '../requests/userAuctionAuthorizationStatusRequest';
import { UserBid } from '../models/userBid';
import { LoggingService } from './LoggingService';


@Injectable({ providedIn: 'root' })
export class AuctionService {

  private auctionAuthorizationCache: number[] | undefined;
  private auctionAuthorizationObservableCache?: Observable<number[] | undefined>;
  private auctionPaymentCache: number[] | undefined;
   
  constructor(private amApiService:AuctionManagementApiService, private authorizationApiService:AuthorizationApiService, private authService:AuthenticationService, 
    private propertyService:PropertyService, private snackBar: MatSnackBar, private logger:LoggingService){
    this.authService.currentUserSubject.subscribe(au=>this.processCurrentUser(au));
  }

  get authorized(){
    return this.authService.authorizedSubject.value;
  }

  private processCurrentUser(au:ApplicationUser|undefined){
    if(!au){
        //user logged out, clear all personal caches
        this.clearPersonalCache();
    }
  }

  private clearPersonalCache(){
      this.auctionAuthorizationCache=undefined;
      this.auctionAuthorizationObservableCache=undefined;
      this.auctionPaymentCache=undefined;
  }

  public placeBid(bid:BidRequest, p:Property):Observable<any>{
    return this.amApiService.placeBid(bid).pipe(map(r=> this.processBidResult(r, p)));
  }

  private processBidResult(br:BidResponse, p:Property){
    if(br.code==3 || br.code==4 || br.code==5 || br.code==6 || br.code==7){
      //add bid to users bid array
      this.authService.currentUserValue!.activeBids.unshift(UserBid.mapFromDTO(br.userBid));
    };
    this.logger.log(br);
    switch(br.code){
      case 0:
        //Not authorized
        this.snackBar.open('Offer rejected - You are not authorized for this auction', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
      case 1:
        //Auction ended
        this.snackBar.open('Auction has ended', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
      case 2:
        //Bid too low
        this.snackBar.open('Your bid is too low', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 3:
        //You were outbid
        if(br.bigCode==3) this.snackBar.open('You have entered a bid equal to a bid received before we received yours. To avoid recording two bids of the same amount, your bid has been recorded at $1 less than the bid amount you entered. You may place a new bid equal to or higher than the current minimum bid.', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        else this.snackBar.open('You were outbid!', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 4:
        //You are highest bidder
        this.snackBar.open('You are the highest bidder!', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 5:
        //You increased your bid
        this.snackBar.open('You increased your bid', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 6:
        //You increased your maximum (proxy) bid
        this.snackBar.open('You increased your maximum bid', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 7:
        //You decreased your bid
        this.snackBar.open('You decreased your maximum bid', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 8:
          //You cannot lower your bid
          this.snackBar.open('You cannot lower your bid', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
          break;
      default:
        //Unknown error
        this.snackBar.open('An error occured:' + br.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
    }
    return br;
  }

  public makeOffer(bid:BidRequest, p:Property):Observable<any>{
    return this.amApiService.makeOffer(bid).pipe(map(r=>this.processOfferResult(r, p)));
  }

  private processOfferResult(br:BidResponse, p:Property){
    if(br.code==5 || br.code==6){
      //add bid to users bid array
      this.authService.currentUserValue!.activeBids.unshift(UserBid.mapFromDTO(br.userBid));
    };
    this.logger.log(br);
    switch(br.code){
      case 0:
        //Not authorized
        this.snackBar.open('Offer rejected - You are not authorized for this auction', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 1:
        //Auction ended
        this.snackBar.open('Offer rejected - Auction has ended', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 2:
        //Sale pending
        this.snackBar.open('Offer rejected - Sale pending', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 3:
        //Bids suspended
        this.snackBar.open('Offer rejected - Bids suspended', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 4:
        //Amount too low - Rejected
        this.snackBar.open('Offer rejected - Amount too low or lower than last offer', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 5:
        //Offer accepted
        this.snackBar.open('Offer accepted', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 6:
        //Counter offer
        this.snackBar.open('You received a counter offer', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      default:
        //Unknown error
        this.snackBar.open('An error occured:' + br.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
    }
    return br;
  }

  public acceptOffer(accept:AcceptRequest, p:Property):Observable<any>{
    return this.amApiService.acceptOffer(accept).pipe(map(r=>this.processAcceptOfferResult(r, accept, p)));
  }

  private processAcceptOfferResult(r:Result, accept:AcceptRequest, p:Property){
    this.logger.log(r);
    if(r.success){
      //update user bid
      let userBid = this.authService.currentUserValue!.activeBids.find(b=>b.id==accept.bidId);
      if(userBid) userBid.askAccepted=true;
    };
    switch(r.code){
      case 1:
        //Bid does not exist
        this.snackBar.open('Offer not found', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
      case 2:
        //Offer acceptance deadline exceeded
        this.snackBar.open('Offer acceptance deadline exceeded', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 3:
        //Offer acceptance processed
        this.snackBar.open('Acceptance processed', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
        default:
        //Unknown error
        this.snackBar.open('An error occured:' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
    }
    return r;
  }

  public buyNow(bid:BidRequest, p:Property):Observable<any>{
    return this.amApiService.buyNow(bid).pipe(map(r=>this.processBuyNowResult(r, p)));
  }

  private processBuyNowResult(br:BidResponse, p:Property){
    if(br.code==5){
      //add bid to users bid array
      this.authService.currentUserValue!.activeBids.unshift(UserBid.mapFromDTO(br.userBid));
    };
    this.logger.log(br);
    switch(br.code){
      case 0:
        //Not authorized
        this.snackBar.open('Buy Now rejected - You are not authorized for this auction', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
      case 1:
        //Auction ended
        this.snackBar.open('Buy Now rejected - Auction has ended', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
      case 2:
        //Sale pending
        this.snackBar.open('Buy Now rejected - Sale Pending', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 3:
        //Bids suspended
        this.snackBar.open('Buy Now rejected - Bids suspended', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 4:
        //Price mismatch
        this.snackBar.open('Buy Now rejected - Offer not matching asking price', '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      case 5:
        //Buy Now accepted
        this.snackBar.open('Buy Now accepted', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        break;
      default:
        //Unknown error
        this.snackBar.open('An error occured:' + br.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        break;
    }
    return br;
  }

  public processBidPublishingData(bpd:BidPublishingData, property:Property){
    property.auction?.updateAuction(bpd, property.auction?.ownBidderKey); //live bids carry bidder keys
    //we need to update the ranking information;
    this.getAuctionBidRanking(property).pipe(take(1)).subscribe();
  }

  public processBidEqualizedPublishingData(bpd:BidPublishingData){
    const bid = this.authService.currentUserValue?.activeBids.find(b=>b.id==bpd.auctionBid!.id);
    if(bid){
      //if this user owns the bid, then equalize the bid to currentBid;
      bid.bidAmount=bid.currentBid;
    }
  }

  public processAuctionStatus(spd:AuctionStatusPublishingData, property:Property){
    property.auction!.updateAuctionStatus(spd);
  }

  public processAuctionEndPostponedPublishingData(spd:AuctionStatusPublishingData, property:Property){
    property.auction!.updateAuctionEndPostponed(spd);
  }
  
  public getAuctionEnding(auctionId:number, scheduleSeqNo:number){
    return this.amApiService.getAuctionEnding(auctionId, scheduleSeqNo);
  }

  public getAuctionBidHistory(property:Property){
    //bids carry bidder keys instead of user ids: the own key (logged in users only) marks the own bids
    const ownBidderKey$:Observable<string|undefined> = this.authService.currentUserValue
      ? this.amApiService.getBidderKey(property.auction!.id).pipe(map((r:any) => r?.bidderKey ?? undefined))
      : of(undefined);
    return forkJoin([this.amApiService.getAuctionBidHistory(property.auction!.id,property.auction!.scheduleSeqNo!), ownBidderKey$]).pipe(map(([r, ownBidderKey])=>{
      property.auction!.ownBidderKey = ownBidderKey;
      return this.processAuctionBidHistory(property, r as AuctionBidDTO[]);
    }), shareReplay(1));
  }
  private processAuctionBidHistory(property:Property, dtoArray:AuctionBidDTO[]){
    //logic here: when we receive the bidding history, there is a small chance we received an updated bid in the meanwhile (race condition).
    //therfor we can't just replace the array, but we need to preserve the existing bid entries and avoid duplicates.
    property.mappingFinishedSubject.pipe(takeWhile(finished=>finished==false, true)).subscribe(finished=>{
      if(finished){
        if(!property.auction!.bids) property.auction!.bids=dtoArray.map(dto=>AuctionBid.mapFromDTO(dto, property.auction!.ownBidderKey));
        else{
          dtoArray.forEach(dto=>{
            var bid = AuctionBid.mapFromDTO(dto, property.auction!.ownBidderKey)
            var existingBidIndex = property.auction!.bids!.findIndex(b=>b.id==bid.id && b.currentBid==bid.currentBid);
            if(existingBidIndex>-1) property.auction!.bids![existingBidIndex]=bid; //don't touch bids we don't find as they might have come in just now
            else property.auction!.bids!.push(bid);
          });
          property.auction!.bids!.sort((bidA,bidB)=>{return Bid.compare(bidA,bidB);});
        }
      }
    }); 
  }

  public getAuctionBidRanking(property:Property){
    return this.amApiService.getAuctionBidRanking(property.auction!.id,property.auction!.scheduleSeqNo!).pipe(map((r)=>this.processAuctionBidRanking(property, r)), shareReplay(1));
  }
  private processAuctionBidRanking(property:Property, idArray:number[]){
    property.mappingFinishedSubject.pipe(takeWhile(finished=>finished==false, true)).subscribe(finished=>{
      if(finished){
        property.auction!.updateAuctionBidRanking(idArray);
      }
    }); 
  }

  //just for admin views
  public getPropertyBidHistory(propertyId:number){
    return this.amApiService.getPropertyBidHistory(propertyId).pipe(map((r)=>this.processPropertyBidHistory(r)),  shareReplay(1));
  }
  /** masked bid history (one entry per auction sequence) for users who may not see the full history (client users) */
  public getPropertyBidHistoryAnonymized(propertyId:number){
    return this.amApiService.getPropertyBidHistoryAnonymized(propertyId).pipe(map((dto:any)=>PropertyBidHistoryAnonymized.mapFromDTO(dto as PropertyBidHistoryAnonymizedDTO)));
  }

  private processPropertyBidHistory(dto:PropertyBidHistoryDTO){
    return PropertyBidHistory.mapFromDTO(dto);
  }

  public getUserAuctionAuthorizationStatus(property:Property){
    let request=new UserAuctionAuthorizationStatusRequest();
    request.purchaseAgreementRequired= property.auction!.purchaseAgreementTemplateId!=undefined && property.auction!.purchaseAgreementTemplateId>0;
    request.propertyId=property.id;
    request.auctionId=property.auction!.id;
    request.scheduleSequenceNo=property.auction!.scheduleSeqNo;
    return this.propertyService.userAuctionAuthorizationStatus(request);
  }

  public verifyUserAuthorizationForAuction(auctionId:number){
    //TODO: this is getting more involved now. Need to verify if we need to execute a Purchase Agreement
    if(this.authService.currentUserValue?.canSkipAuctionAuthorization) return of(true); //super admins don't need authorization
    else return this.getUserAuctionAuthorizations().pipe(map(a=> a?.length && a.includes(auctionId)));
  }

  public getUserAuctionAuthorizations(){
    if(!this.authorized) return of([]);
    // Data available
    if (this.auctionAuthorizationCache) return of(this.auctionAuthorizationCache);
    // Request pending
    else if (this.auctionAuthorizationObservableCache) return this.auctionAuthorizationObservableCache;
    // New request needed
    else this.auctionAuthorizationObservableCache = this.fetchUserAuctionAuthorizations();

    return this.auctionAuthorizationObservableCache;
  }

  private fetchUserAuctionAuthorizations(){
    return this.authorizationApiService.getUserAuctionAuthorizations().pipe(map(r=>this.processUserAuctionAuthorizationsResult(r)), shareReplay(1));
  }

  private processUserAuctionAuthorizationsResult(r:number[]){
      this.auctionAuthorizationCache=r;
      return this.auctionAuthorizationCache;
  }

  public requestPrivateAuctionAuthorization(bidderId:string, auctionId:number, propertyId:number, ){
    let request = new PrivateAuctionAuthorizationRequest();
    request.bidderId=bidderId;
    request.auctionId=auctionId;
    request.propertyId=propertyId;
    return this.amApiService.requestPrivateAuctionAuthorization(request).pipe(map((r:PrivateAuctionAuthorizationResponse) => {
      if(r.success){
        this.snackBar.open('Your request has been submitted', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
      }
      else{
        this.snackBar.open(r.message!, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
      return r;
    }));
  }

  public verifyPrivateAuctionAuthorization(bidderId:string, auctionId:number, propertyId:number, ){
    let request = new PrivateAuctionAuthorizationRequest();
    request.bidderId=bidderId;
    request.auctionId=auctionId;
    request.propertyId=propertyId;
    return this.amApiService.verifyPrivateAuctionAuthorization(request).pipe(map((r:PrivateAuctionAuthorizationResponse) => {
      return r;
    }));
  }

  public updatePrivateAuctionAuthorizationStatus(id:number, bidderId:string, auctionId:number, propertyId:number, status:boolean, reason:string){
    let request = new UpdatePrivateAuctionAuthorizationStatusRequest();
    request.id=id;
    request.bidderId=bidderId;
    request.auctionId=auctionId;
    request.propertyId=propertyId;
    request.status=status;
    request.reason=reason;
    return this.amApiService.updatePrivateAuctionAuthorizationStatus(request).pipe(map((r:PrivateAuctionAuthorizationResponse) => {
      if(r.success){
        this.snackBar.open('Status has been updated', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
      }
      else{
        this.snackBar.open(r.message!, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
      return r;
    }))
  }

  public getPrivateAuctionAuthorizations(auctionId:number, userId:string|undefined){
    return this.amApiService.getPrivateAuctionAuthorizations(auctionId,userId).pipe(map((r:PrivateAuctionAuthorizationDTO[]) => r), shareReplay(1))
  }

  public equalizeUserBidForAuction(bidderId:string, auctionId:number, reason:string){
    let request = new EqualizeUserBidForAuctionRequest();
    request.bidderId=bidderId;
    request.auctionId=auctionId;
    request.reason=reason;
    return this.amApiService.equalizeUserBidForAuction(request).pipe(map((r:BasicResponse)=>{
      if(r.success){
        this.snackBar.open('Proxy has been removed', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
      }
      else{
        this.snackBar.open(r.message!, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
      return r;
    }));
  }

  //#region stripe
  public authorizeForAuctionStripe(auctionId:number, scheduleSequenceNo:number, amount:number, gatewayReference:string, gatewayResponse:string, success:boolean, termsAccepted:boolean){
    let request=new AuctionAuthorizationRequest();
    request.id=0;
    request.auctionId = auctionId;
    request.scheduleSeqNo = scheduleSequenceNo;
    request.amount = amount;
    request.gatewayReference = gatewayReference;
    request.gatewayResponse = gatewayResponse;
    request.success = success;
    request.termsAccepted = termsAccepted;
    return this.authorizationApiService.authorizeForAuctionStripe(request).pipe(map(r=>this.processAuctionAuthorizationStripeResult(r,request)));
  }

  private processAuctionAuthorizationStripeResult(r:Result, ar:AuctionAuthorizationRequest){
    if(r.success){
      if(this.auctionAuthorizationCache==undefined)this.auctionAuthorizationCache=[];
      this.auctionAuthorizationCache.push(ar.auctionId);
    }
    return r
  }

  public createPaymentIntentStripe(auctionId:number, scheduleSequenceNo:number, amount:number, auctionDescription:string){
    let request = new CreatePaymentIntentStripeRequest();
    request.auctionId = auctionId;
    request.scheduleSequenceNo = scheduleSequenceNo;
    request.amount = amount;
    request.auctionDescription = auctionDescription
    return this.authorizationApiService.createPaymentIntentStripe(request).pipe(map(r=>this.processPaymentIntentStripeResponse(r)), shareReplay(1));
  }

  private processPaymentIntentStripeResponse(r:CreatePaymentIntentStripeResponse){
    this.logger.log(r);
    return r;
  }
  //#endregion

  //#region paypal
  public authorizeForAuctionPaypal(auctionId:number, scheduleSequenceNo:number, amount:number, gatewayReference:string, gatewayResponse:string, success:boolean, termsAccepted:boolean){
    let request=new AuctionAuthorizationRequest();
    request.id=0;
    request.auctionId = auctionId;
    request.scheduleSeqNo = scheduleSequenceNo;
    request.amount = amount;
    request.gatewayReference = gatewayReference;
    request.gatewayResponse = gatewayResponse;
    request.success = success;
    request.termsAccepted = termsAccepted;
    return this.authorizationApiService.authorizeForAuctionPaypal(request).pipe(map(r=>this.processAuctionAuthorizationPaypalResult(r,request)), shareReplay(1));
  }

  private processAuctionAuthorizationPaypalResult(r:Result, ar:AuctionAuthorizationRequest){
    if(r.success){
      if(this.auctionAuthorizationCache==undefined)this.auctionAuthorizationCache=[];
      this.auctionAuthorizationCache.push(ar.auctionId);
    }
    return r
  }

  public createPaymentIntentPaypal(auctionId:number, scheduleSequenceNo:number, amount:number, auctionDescription:string){
    let request = new CreatePaymentIntentPaypalRequest();
    request.auctionId = auctionId;
    request.scheduleSequenceNo = scheduleSequenceNo;
    request.amount = amount;
    request.auctionDescription = auctionDescription
    return this.authorizationApiService.createPaymentIntentPaypal(request).pipe(map(r=>this.processPaymentIntentPaypalResponse(r)), shareReplay(1));
  }

  private processPaymentIntentPaypalResponse(r:CreatePaymentIntentPaypalResponse){
    this.logger.log(r);
    return r;
  }
  //#endregion

  //#region plaid
  public getPlaidLinkToken(){
    return this.authorizationApiService.createLinkTokenPlaid(this.authService.currentUserValue!.id,this.authService.currentUserValue!.email).pipe(map(r=>this.processPlaidLinkTokenResponse(r)), shareReplay(1));
  }

  private processPlaidLinkTokenResponse(r:LinkTokenResponse){
    //nothing to process yet
    return r
  }

  public exchangePublicToken(publicToken:string){
    return this.authorizationApiService.exchangePublicTokenPlaid(this.authService.currentUserValue!.id,publicToken).pipe(map(r=>this.processExchangePublicTokenResponse(r)), shareReplay(1));
  }

  private processExchangePublicTokenResponse(r:any){
    //nothing to process yet
    return r;
  }

  public authorizeForAuctionPlaid(auctionId:number, scheduleSequenceNo:number, amount:number, termsAccepted:boolean){
    let request=new AuctionAuthorizationRequest();
    request.id=0;
    request.auctionId = auctionId;
    request.scheduleSeqNo = scheduleSequenceNo;
    request.amount = amount;
    request.termsAccepted = termsAccepted;
    return this.authorizationApiService.authorizeForAuctionPlaid(request).pipe(map(r=>this.processAuctionAuthorizationPlaidResult(r,request)), shareReplay(1));
  }

  private processAuctionAuthorizationPlaidResult(r:Result, ar:AuctionAuthorizationRequest){
    if(r.success){
      if(this.auctionAuthorizationCache==undefined)this.auctionAuthorizationCache=[];
      this.auctionAuthorizationCache.push(ar.auctionId);
    }
    return r
  }

  public paymentForAuctionPlaid(auctionId:number, scheduleSequenceNo:number, amount:number){
    let request=new AuctionPaymentRequest();
    request.id=0;
    request.auctionId = auctionId;
    request.scheduleSeqNo = scheduleSequenceNo;
    request.amount = amount;
    return this.authorizationApiService.paymentForAuctionPlaid(request).pipe(map(r=>this.processAuctionPaymentPlaidResult(r,request)), shareReplay(1));
  }

  private processAuctionPaymentPlaidResult(r:Result, ar:AuctionPaymentRequest){
    if(r.success){
      if(this.auctionPaymentCache==undefined)this.auctionPaymentCache=[];
      this.auctionPaymentCache.push(ar.auctionId);
    }
    return r
  }
  //#endregion
}